// @file Network requests related to oEmbed endpoint from surface

import { fetchJson } from '@@/surface/api_fetch'
import { HTTPMethod } from '@padlet/fetch'

interface OEmbedResponse {
  type: 'rich'
  title: string
  description: string
  author_name: string
  author_url?: string
  thumbnail_url: string
  thumbnail_width: number
  thumbnail_height: number
  html?: string
  width: number
  height: number
  name: string
  url: string
  provider_name: string
  provider_url: string
  version: string
}

interface OEmbedRequestParams {
  url: string
  maxwidth?: number
  maxheight?: number
}

interface EmbedUrlResponse {
  embedUrl: string
}

class OEmbedApi {
  /**
   * Fetches oEmbed information for a given URL
   * @param params - The URL to fetch oEmbed data for, with optional maxwidth and maxheight
   * @returns Promise containing oEmbed data including embed code, thumbnail, title, etc.
   */
  public static async fetch(params: OEmbedRequestParams): Promise<OEmbedResponse> {
    const queryParams = new URLSearchParams({
      url: params.url,
      ...(params.maxwidth && { maxwidth: params.maxwidth.toString() }),
      ...(params.maxheight && { maxheight: params.maxheight.toString() }),
    })

    return await fetchJson(`/oembed?${queryParams.toString()}`, {
      method: HTTPMethod.get,
    })
  }

  /**
   * Fetches only the embed URL for a given URL without access checks
   * This is useful for private padlets where we still want to show the embed frame
   * @param url - The URL to get the embed URL for
   * @returns Promise containing the embed URL
   */
  public static async fetchEmbedUrl(url: string): Promise<EmbedUrlResponse> {
    const queryParams = new URLSearchParams({ url })

    return await fetchJson(`/oembed/embed-url?${queryParams.toString()}`, {
      method: HTTPMethod.get,
    })
  }
}

export { OEmbedApi }
export type { OEmbedResponse, OEmbedRequestParams, EmbedUrlResponse }
